import * as React from 'react';
import { isDeveloper, isManager, useAuth } from '@/shared/lib/auth-context';
import { useServiceFlags } from '@/shared/api/useFeatures';
import { useTheme } from '@/shared/lib/theme';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MenuItem } from '@/components/ui/menu-item';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import {
    DoorOpen,
    Ellipsis,
    LogOut,
    Moon,
    Plus,
    Search,
    Sun,
    Trash2,
    X,
} from 'lucide-react';

import { ImpersonateDialog } from '../ImpersonateDialog';
import { ProfileDeleteDialog } from '../ProfileDeleteDialog';
import { StatusPill } from '../StatusPill';
import { DashboardEntryButton } from './DashboardEntryButton';
import { SidebarCollapseButton } from './SidebarCollapseButton';
import { PinIcon } from './PinIcon';
import type { Profile, ProfileStatus } from '../../types';
import { useNavigate } from 'react-router';

type ProfileSidebarItemProps = {
    profile: Profile & { status: ProfileStatus };
    isSelected: boolean;
    isPinned: boolean;
    isTrackingStatus: boolean;
    onSelect: (id: string) => void;
    onTogglePinned: (id: string) => void;
    onDelete: (id: string) => void;
    registerTourTarget?: (id: string, el: HTMLElement | null) => void;
};

const ProfileSidebarItem = React.memo(function ProfileSidebarItem({
    profile,
    isSelected,
    isPinned,
    isTrackingStatus,
    onSelect,
    onTogglePinned,
    onDelete,
    registerTourTarget,
}: ProfileSidebarItemProps) {
    const statusRef = React.useCallback(
        (el: HTMLElement | null) => {
            // Only the selected row owns sidebar-status; others must not clear it.
            if (!isSelected) return;
            registerTourTarget?.('sidebar-status', el);
        },
        [isSelected, registerTourTarget],
    );

    return (
        <SidebarMenuItem className="w-full max-w-full">
            <SidebarMenuButton
                type="button"
                isActive={isSelected}
                className={cn(
                    'group/profile w-full h-11 rounded-m p-3 text-body1 font-medium gap-2 relative cursor-pointer',
                    isSelected && 'bg-neutral-background-100 text-primary-400!'
                )}
                onClick={() => onSelect(profile.id)}
            >
                <span className="min-w-0 shrink truncate">
                    {profile.title}
                </span>
                <div className="ml-auto flex shrink-0 items-center justify-center gap-1">
                    {isPinned && (
                        <PinIcon
                            className="size-3 shrink-0 text-neutral-text-500"
                            aria-label="Закреплён"
                        />
                    )}
                    {isTrackingStatus && (
                        <span
                            ref={statusRef}
                            className="inline-flex"
                            data-tour-target={isSelected ? 'sidebar-status' : undefined}
                        >
                            <StatusPill status={profile.status} />
                        </span>
                    )}
                    <ProfileActionsMenu
                        isPinned={isPinned}
                        onTogglePinned={() => onTogglePinned(profile.id)}
                        onDelete={() => onDelete(profile.id)}
                    />
                </div>
            </SidebarMenuButton>
        </SidebarMenuItem>
    );
});

export function ProfileActionsMenu({
    isPinned,
    onTogglePinned,
    onDelete,
}: {
    isPinned: boolean;
    onTogglePinned: () => void;
    onDelete?: () => void;
}) {
    const [open, setOpen] = React.useState(false);
    const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const cancelClose = React.useCallback(() => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    }, []);
    const scheduleClose = React.useCallback(() => {
        cancelClose();
        closeTimer.current = setTimeout(() => setOpen(false), 150);
    }, [cancelClose]);

    React.useEffect(() => () => cancelClose(), [cancelClose]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverAnchor asChild>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Действия с профилем"
                    aria-haspopup="menu"
                    className={cn(
                        'ml-1 h-7 w-7 text-neutral-text-500 hover:text-neutral-text-900',
                        'opacity-0 group-hover/profile:opacity-100 focus-visible:opacity-100',
                        open && 'opacity-100',
                    )}
                    onMouseEnter={() => {
                        cancelClose();
                        setOpen(true);
                    }}
                    onMouseLeave={scheduleClose}
                    onClick={(event) => {
                        event.stopPropagation();
                        setOpen(true);
                    }}
                >
                    <Ellipsis strokeWidth={2.5} className="size-4" />
                </Button>
            </PopoverAnchor>
            <PopoverContent
                role="menu"
                align="end"
                side="right"
                className="w-52 p-1"
                onOpenAutoFocus={(event) => event.preventDefault()}
                onMouseEnter={cancelClose}
                onMouseLeave={scheduleClose}
                onClick={(event) => event.stopPropagation()}
            >
                <MenuItem
                    onClick={() => {
                        onTogglePinned();
                        setOpen(false);
                    }}
                >
                    <PinIcon className="size-4" />
                    {isPinned ? 'Открепить' : 'Закрепить'}
                </MenuItem>
                {onDelete && (
                    <MenuItem
                        variant="destructive"
                        onClick={() => {
                            onDelete();
                            setOpen(false);
                        }}
                    >
                        <Trash2 className="size-4" />
                        Удалить
                    </MenuItem>
                )}
            </PopoverContent>
        </Popover>
    );
}

export function RecsysSidebar({
    profiles,
    selectedProfileId,
    onSelectProfile,
    onCreateProfile,
    onDeleteProfile,
    isPinned,
    onTogglePinned,
    sortProfilesWithPins,
    isLoadingProfiles,
    profilesError,
    profileMetaById,
    registerTourTarget,
    tourActiveTarget,
}: {
    profiles: Array<Profile & { status: ProfileStatus }>;
    selectedProfileId: string;
    onSelectProfile: (id: string) => void;
    onCreateProfile: () => void;
    isLoadingProfiles: boolean;
    profilesError: string | null;
    onDeleteProfile?: (id: string) => void;
    isPinned: (id: string) => boolean;
    onTogglePinned: (id: string) => void;
    sortProfilesWithPins: <T extends { id: string }>(items: T[]) => T[];
    profileMetaById: Record<string, import('../../types').ProfileMeta>;
    registerTourTarget?: (id: string, el: HTMLElement | null) => void;
    /** When set, keep the matching tour anchor in the DOM (e.g. close search). */
    tourActiveTarget?: string | null;
}) {
    const { state, toggleSidebar } = useSidebar();
    const { toggle, theme } = useTheme();
    const navigate = useNavigate();
    const features = useServiceFlags();
    const auth = useAuth();
    const [isSearchActive, setIsSearchActive] = React.useState(false);
    const [query, setQuery] = React.useState('');
    const [deletingProfileId, setDeletingProfileId] = React.useState<string | null>(null);
    const [impersonateOpen, setImpersonateOpen] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const isCollapsed = state === 'collapsed';
    const showImpersonate = isDeveloper(auth.user);

    const requestDelete = React.useCallback(
        (id: string) => {
            const isDraft = profileMetaById?.[id]?.isInDatabase !== true;
            if (isDraft) {
                onDeleteProfile?.(id);
                return;
            }
            setDeletingProfileId(id);
        },
        [onDeleteProfile, profileMetaById],
    );
    const filtered = React.useMemo(() => {
        const q = query.trim().toLowerCase();
        const matched = !q
            ? profiles
            : profiles.filter((p) => p.title.toLowerCase().includes(q));
        return sortProfilesWithPins(matched);
    }, [profiles, query, sortProfilesWithPins]);

    React.useEffect(() => {
        if (!isSearchActive) return;
        inputRef.current?.focus();
    }, [isSearchActive]);

    const ThemeIcon = theme === 'dark' ? Moon : Sun;

    const activateSearch = () => {
        if (isCollapsed) toggleSidebar();
        setIsSearchActive(true);
    };

    const closeSearch = () => {
        setIsSearchActive(false);
        setQuery('');
    };

    React.useEffect(() => {
        if (tourActiveTarget === 'new-profile' && isSearchActive) {
            closeSearch();
        }
    }, [tourActiveTarget, isSearchActive]);

    const newProfileRef = React.useCallback(
        (el: HTMLButtonElement | null) => {
            registerTourTarget?.('new-profile', el);
        },
        [registerTourTarget],
    );

    const handleLogout = React.useCallback(() => {
        auth.signOut();
    }, [auth]);

    const isGuest = Boolean(auth.user?.is_guest);
    const accountLabel = isGuest ? 'Гость' : (auth.user?.username ?? 'managermail@example.com');
    const logoutLabel = isGuest ? 'Начать заново' : 'Выйти';

    return (
        <>
            <Sidebar variant="inset" collapsible="minify">
                <SidebarHeader>
                    {isCollapsed ? (
                        <div className="flex flex-col gap-3 items-center">
                            <SidebarCollapseButton />
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="hover:bg-neutral-background-100"
                                onClick={activateSearch}
                                aria-label="Поиск"
                            >
                                <Search strokeWidth={2.5} className="h-5 w-5 text-neutral-text-800" />
                            </Button>
                            <Button
                                ref={newProfileRef}
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="hover:bg-neutral-background-100"
                                data-tour-target="new-profile"
                                onClick={() => {
                                    toggleSidebar();
                                    onCreateProfile();
                                }}
                                aria-label="Новый клиент"
                            >
                                <Plus strokeWidth={2.5} className="h-5 w-5 text-neutral-text-800" />
                            </Button>
                        </div>
                    ) : (
                        <div className="flex w-full gap-2">
                            {!isSearchActive ? (
                                <>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="bg-neutral-background-100 hover:opacity-80"
                                        onClick={activateSearch}
                                        aria-label="Поиск"
                                    >
                                        <Search strokeWidth={2.5} className="h-5 w-5 text-neutral-text-800" />
                                    </Button>

                                    <Button
                                        ref={newProfileRef}
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="w-full flex-1 justify-start pl-3.5! bg-neutral-background-100 hover:opacity-80"
                                        data-tour-target="new-profile"
                                        onClick={onCreateProfile}
                                    >
                                        <Plus strokeWidth={2.5} className="h-5 w-5 text-neutral-text-800" />
                                        <span className="text-neutral-text-800">Новый клиент</span>
                                    </Button>

                                    <SidebarCollapseButton />
                                </>
                            ) : (
                                <div className="flex w-full gap-2">
                                    <div className="relative flex-1">
                                        <Search
                                            strokeWidth={2.5}
                                            className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-text-500"
                                        />
                                        <Input
                                            ref={inputRef}
                                            value={query}
                                            onChange={(e) => setQuery(e.target.value)}
                                            placeholder="Поиск"
                                            className="h-11 rounded-m bg-neutral-background-100! hover:ring-0! pl-10 placeholder:text-neutral-text-500 focus-visible:ring-0 focus-visible:ring-offset-0"
                                        />
                                    </div>

                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={closeSearch}
                                        aria-label="Закрыть поиск"
                                    >
                                        <X strokeWidth={2.5} className="h-5 w-5 text-neutral-text-800" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </SidebarHeader>

                {!isCollapsed && (
                    <>
                        <SidebarContent>
                            <ScrollArea className="h-full">
                                <SidebarMenu>
                                    {filtered.map((profile) => {
                                        const meta = profileMetaById?.[profile.id];

                                        return (
                                            <ProfileSidebarItem
                                                key={profile.id}
                                                profile={profile}
                                                isSelected={profile.id === selectedProfileId}
                                                isPinned={isPinned(profile.id)}
                                                isTrackingStatus={meta?.isTrackingStatus ?? false}
                                                onSelect={onSelectProfile}
                                                onTogglePinned={onTogglePinned}
                                                onDelete={requestDelete}
                                                registerTourTarget={registerTourTarget}
                                            />
                                        );
                                    })}

                                    {isLoadingProfiles && (
                                        <div className="p-3 text-body1 font-medium text-neutral-text-500">
                                            Загружаем профили…
                                        </div>
                                    )}

                                    {!isLoadingProfiles && profilesError && (
                                        <div className="p-3 text-body1 font-medium text-destructive" role="alert" aria-live="assertive">
                                            {profilesError}
                                        </div>
                                    )}

                                    {!isLoadingProfiles && !profilesError && filtered.length === 0 && (
                                        <div className="p-3 text-body1 font-medium text-neutral-text-500">
                                            Ничего не найдено
                                        </div>
                                    )}
                                </SidebarMenu>
                            </ScrollArea>
                        </SidebarContent>
                    </>
                )}

                <SidebarFooter className="mt-auto overflow-visible">
                    {!isCollapsed && (
                        <div className="-mx-2">
                            <Separator />
                        </div>
                    )}

                    {isCollapsed ? (
                        <div className="flex flex-col gap-2 items-center justify-center overflow-visible pt-2">
                            {isManager(auth.user) && features.dashboard && (
                                <DashboardEntryButton
                                    onClick={() => navigate('/dashboard')}
                                    registerTourTarget={registerTourTarget}
                                />
                            )}
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="hover:bg-neutral-background-100"
                                        onClick={toggle}
                                        aria-label="Сменить тему"
                                    >
                                        <ThemeIcon strokeWidth={2.5} className="h-5 w-5 text-neutral-text-800" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="right" align="center" className="ml-1">
                                    Сменить тему
                                </TooltipContent>
                            </Tooltip>
                            {showImpersonate && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="hover:bg-neutral-background-100"
                                            onClick={() => setImpersonateOpen(true)}
                                            aria-label="Открыть другой аккаунт"
                                        >
                                            <DoorOpen strokeWidth={2.5} className="h-5 w-5 text-neutral-text-800" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="right" align="center" className="ml-1">
                                        Открыть другой аккаунт
                                    </TooltipContent>
                                </Tooltip>
                            )}
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="hover:bg-neutral-background-100"
                                        onClick={handleLogout}
                                        aria-label={logoutLabel}
                                    >
                                        <LogOut strokeWidth={2.5} className="h-5 w-5 text-neutral-text-800" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="right" align="center" className="ml-1">
                                    {logoutLabel}
                                </TooltipContent>
                            </Tooltip>
                        </div>
                    ) : (
                        <div className="flex items-center justify-between gap-2 w-full">
                            <div className="min-w-0 flex-1" title={accountLabel}>
                                <span className="ml-3 truncate block">{accountLabel}</span>
                            </div>

                            <div className="flex items-center shrink-0 overflow-visible">
                                {isManager(auth.user) && features.dashboard && (
                                    <DashboardEntryButton
                                        onClick={() => navigate('/dashboard')}
                                        registerTourTarget={registerTourTarget}
                                    />
                                )}
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="transition-opacity hover:bg-neutral-background-100"
                                    onClick={toggle}
                                    title="Сменить тему"
                                    aria-label="Сменить тему"
                                >
                                    <ThemeIcon strokeWidth={2.5} className="h-5 w-5 text-neutral-text-800" />
                                </Button>
                                {showImpersonate && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="transition-opacity hover:bg-neutral-background-100"
                                        onClick={() => setImpersonateOpen(true)}
                                        title="Открыть другой аккаунт"
                                        aria-label="Открыть другой аккаунт"
                                    >
                                        <DoorOpen strokeWidth={2.5} className="h-5 w-5 text-neutral-text-800" />
                                    </Button>
                                )}
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="transition-opacity hover:bg-neutral-background-100"
                                    onClick={handleLogout}
                                    title={logoutLabel}
                                    aria-label={logoutLabel}
                                >
                                    <LogOut strokeWidth={2.5} className="h-5 w-5 text-neutral-text-800" />
                                </Button>
                            </div>
                        </div>
                    )}
                </SidebarFooter>
            </Sidebar>
            {!isCollapsed && <Separator orientation="vertical" className="h-full" />}

            <ImpersonateDialog open={impersonateOpen} onOpenChange={setImpersonateOpen} />

            <ProfileDeleteDialog
                open={deletingProfileId !== null}
                onOpenChange={(open) => {
                    if (!open) setDeletingProfileId(null);
                }}
                onDelete={() => {
                    if (deletingProfileId && onDeleteProfile) {
                        onDeleteProfile(deletingProfileId);
                    }
                    setDeletingProfileId(null);
                }}
            />
        </>
    );
}
